import { createContext, useContext } from 'react'

export type AuthMode = 'bypass' | 'auth0'

/**
 * Which kind of account is signed in.
 *
 * A role here decides *what the app renders*, never what the caller may do —
 * `/notary/*` and `/admin/*` are both re-checked server-side on every request,
 * so a tampered client gets a 403 rather than data.
 */
export type Role = 'citizen' | 'admin' | 'notary'

export const AUTH_MODE: AuthMode =
  import.meta.env.VITE_AUTH_MODE === 'auth0' ? 'auth0' : 'bypass'

export interface AuthContextValue {
  mode: AuthMode
  /** True while the SDK is restoring a session. Routes show a loading state. */
  isLoading: boolean
  isAuthenticated: boolean
  /**
   * Best-effort identity for the sign-in screens. Authorisation is never
   * decided from this — admin routes are gated on `GET /me` returning
   * `is_admin`, and the server re-checks every request regardless.
   */
  email: string | null
  error: string | null
  /**
   * The role of the signed-in dev persona, or `null` in Auth0 mode where the
   * server's token is authoritative and the client does not guess.
   */
  role: Role | null
  signIn: (options?: { email?: string; role?: Role; returnTo?: string }) => void | Promise<void>
  signOut: () => void | Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>')
  return value
}

/**
 * The identities the backend seeds. Listing them turns "switch persona" from a
 * curl incantation into one click, which is the entire point of bypass mode.
 */
export interface SeededIdentity {
  email: string
  name: string
  role: Role
  blurb: string
}

export const SEEDED_IDENTITIES: SeededIdentity[] = [
  {
    email: 'nisha@example.com',
    name: 'Nisha Rao',
    role: 'citizen',
    blurb: 'Empty account — start the wizard from the very beginning',
  },
  {
    email: 'priya@example.com',
    name: 'Priya Sharma',
    role: 'citizen',
    blurb: 'Has a submitted living will that a notary has accepted',
  },
  {
    email: 'rahul@example.com',
    name: 'Rahul Verma',
    role: 'citizen',
    blurb: 'Submitted and waiting for a notary to be allocated',
  },
  {
    email: 'meera@example.com',
    name: 'Meera Banerjee',
    role: 'citizen',
    blurb: 'Allocated to a notary, awaiting their reply',
  },
  {
    email: 'arun@example.com',
    name: 'Arun Menon',
    role: 'citizen',
    blurb: 'Another citizen account in the Bengaluru zone',
  },
  {
    email: 'vinod@example.com',
    name: 'Vinod Kulkarni',
    role: 'citizen',
    blurb: 'Notarisation already accepted, in the Delhi zone',
  },
  {
    email: 'admin@livingwill.local',
    name: 'Office administrator',
    role: 'admin',
    blurb: 'Sees the allocation queue, notary directory and job history',
  },
  {
    email: 'anand.rao@notary.local',
    name: 'Anand Rao — notary',
    role: 'notary',
    blurb: 'KA-NOT-1001, serves 560001 and 560034. Can claim a job by code.',
  },
  {
    email: 'sunita.hegde@notary.local',
    name: 'Sunita Hegde — notary',
    role: 'notary',
    blurb: 'KA-NOT-1002, serves 560034 only — use to see an out-of-zone claim',
  },
]
